import React from "react";
import { useChannelUpdates } from "./ws/useChannelUpdates";
import type { Output } from "./call";
import call from "./call";
import useQuery, { useGetQueryData, useMutateBody } from "./useQuery";
import { comparatorBy } from "diagnostics-lib/comparator";
const PATH = "/projects";
export type Project = Output<"GET", typeof PATH>["body"]["collection"][number];

const projectsCache = new WeakMap<Project[], Project[]>();
export function useProjects() {
  const projects: Project[] = useQuery(PATH).body.collection;
  const getCachedProjects = useGetQueryData(PATH);
  const fetchUpdatedItems = React.useCallback(async (since: string) => {
    const { collection, tombstoneIds } = (
      await call("GET", PATH, { query: { since: new Date(since) } })
    ).body;
    return [collection, tombstoneIds] as const;
  }, []);
  const mutate = useMutateBody(PATH);

  useChannelUpdates(
    { key: "projects" },
    {
      readItems: React.useCallback(() => getCachedProjects()?.body.collection, [getCachedProjects]),
      fetchUpdatedItems,
      mutateItems: React.useCallback(
        (params: (oldItems: Project[]) => Project[]) =>
          mutate(({ collection, ...rest }) => ({
            collection: params(collection),
            ...rest,
          })),
        [mutate],
      ),
    },
  );

  return React.useMemo(() => {
    const cached = projectsCache.get(projects);
    if (cached) return cached;
    const sortedProjects = [...projects].sort(comparatorBy("name"));
    projectsCache.set(projects, sortedProjects);
    return sortedProjects;
  }, [projects]);
}

export const useIndexedProjects = () => {
  const projects = useProjects();
  return React.useMemo(
    () =>
      projects.reduce<{ [id: string]: Project }>((acc, p) => {
        acc[p.id] = p;
        return acc;
      }, {}),
    [projects],
  );
};

export const useProject = (projectId: string) => useIndexedProjects()[projectId];
